/**
 * Acaia (Lunar, Pearl, Pyxis and relatives) wire format: pure encoding, decoding and frame reassembly, no Web
 * Bluetooth. Reverse-engineered by other projects, never published by Acaia. See docs/acaia-protocol.md for the
 * sources, where they disagree, and the reasoning behind each choice.
 */

// ---------------------------------------------------------------- GATT layouts

/** Classic layout (Lunar before 2021, Pearl): the SIG "Internet Protocol Support" service, reused by Acaia. */
export const ACAIA_CLASSIC_SERVICE_UUID = '00001820-0000-1000-8000-00805f9b34fb';
/** Classic layout: one characteristic (the SIG "Age" UUID) for notifications and commands. */
export const ACAIA_CLASSIC_CHAR_UUID = '00002a80-0000-1000-8000-00805f9b34fb';

/** Newer layout (Pyxis, Lunar 2021, Pearl S): Microchip's "transparent UART" service, which other gadgets use too. */
export const ACAIA_UART_SERVICE_UUID = '49535343-fe7d-4ae5-8fa9-9fafd205e455';
/** Notifications from the scale (Microchip's "TX"). */
export const ACAIA_UART_NOTIFY_CHAR_UUID = '49535343-1e4d-4bd9-ba61-23c647249616';
/** Commands to the scale (Microchip's "RX"). */
export const ACAIA_UART_WRITE_CHAR_UUID = '49535343-8841-43f4-a8d4-ecbe34729bb3';

export type AcaiaLayoutKind = 'classic' | 'uart';

export interface AcaiaLayout {
  kind: AcaiaLayoutKind;
  service: string;
  notifyChar: string;
  writeChar: string;
}

export const ACAIA_LAYOUTS: Readonly<Record<AcaiaLayoutKind, AcaiaLayout>> = {
  classic: { kind: 'classic', service: ACAIA_CLASSIC_SERVICE_UUID, notifyChar: ACAIA_CLASSIC_CHAR_UUID, writeChar: ACAIA_CLASSIC_CHAR_UUID },
  uart: { kind: 'uart', service: ACAIA_UART_SERVICE_UUID, notifyChar: ACAIA_UART_NOTIFY_CHAR_UUID, writeChar: ACAIA_UART_WRITE_CHAR_UUID },
};

/** The services to look for after GATT connect, in order of preference: the classic one first (as the sources check). */
export const ACAIA_PRIMARY_SERVICES: readonly string[] = [ACAIA_CLASSIC_SERVICE_UUID, ACAIA_UART_SERVICE_UUID];

/** The layout a service found on the device belongs to, or null for a service that isn't an Acaia one. */
export function acaiaLayoutForService(uuid: string): AcaiaLayout | null {
  const u = uuid.toLowerCase();
  if (u === ACAIA_CLASSIC_SERVICE_UUID) return ACAIA_LAYOUTS.classic;
  if (u === ACAIA_UART_SERVICE_UUID) return ACAIA_LAYOUTS.uart;
  return null;
}

// ---------------------------------------------------------------- names

/** Advertised name prefixes (Beanconqueror's and AcaiaArduinoBLE's list). Web Bluetooth prefixes are case-sensitive. */
export const ACAIA_NAME_PREFIXES: readonly string[] = ['ACAIA', 'LUNAR', 'PEARL', 'PYXIS', 'PROCH', 'CINCO'];

/** Case-insensitive prefix, ignoring surrounding spaces. */
export function isAcaiaName(name: string | null | undefined): boolean {
  if (!name) return false;
  const n = name.trim().toUpperCase();
  return ACAIA_NAME_PREFIXES.some((p) => n.startsWith(p));
}

// ---------------------------------------------------------------- frames

export const ACAIA_HEADER1 = 0xef;
export const ACAIA_HEADER2 = 0xdd;
/** Header, kind and checksums: the bytes an incoming frame has beyond its length byte's count. */
export const ACAIA_FRAME_OVERHEAD = 5;
/** de1app gives up on longer frames; nothing the scale sends comes close. */
export const ACAIA_MAX_LENGTH_BYTE = 64;
/** Junk can't grow the reassembly buffer past this. */
export const ACAIA_MAX_BUFFER = 512;

/** Outgoing command bytes (byte 2). */
export const AcaiaCommand = {
  heartbeat: 0x00,
  tare: 0x04,
  identify: 0x0b,
  event: 0x0c,
  timer: 0x0d,
} as const;

/** Incoming message kinds (byte 2). */
export const AcaiaKind = {
  info: 0x07,
  settings: 0x08,
  event: 0x0c,
} as const;

/** Event types (the byte after an event's length byte) and record tags. */
export const AcaiaEvent = {
  weight: 0x05,
  battery: 0x06,
  timer: 0x07,
  button: 0x08,
  ack: 0x0b,
} as const;

/** The two checksums: sums of the payload bytes at even and at odd positions, mod 256. */
export function acaiaChecksum(payload: ArrayLike<number>): [number, number] {
  let even = 0;
  let odd = 0;
  for (let i = 0; i < payload.length; i++) {
    if (i % 2 === 0) even += payload[i]! & 0xff;
    else odd += payload[i]! & 0xff;
  }
  return [even & 0xff, odd & 0xff];
}

/** EF DD, the command, the payload, the two checksums. */
export function encodeAcaiaFrame(command: number, payload: ArrayLike<number>): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(payload.length + ACAIA_FRAME_OVERHEAD);
  out[0] = ACAIA_HEADER1;
  out[1] = ACAIA_HEADER2;
  out[2] = command & 0xff;
  for (let i = 0; i < payload.length; i++) out[3 + i] = payload[i]! & 0xff;
  const [even, odd] = acaiaChecksum(payload);
  out[out.length - 2] = even;
  out[out.length - 1] = odd;
  return out;
}

const ascii = (text: string) => Array.from(text, (c) => c.charCodeAt(0));

/** UART scales get digits, classic scales dashes (pyacaia, Beanconqueror, aioacaia; de1app sends digits to both). */
const IDENTIFY_UART = ascii('012345678901234');
const IDENTIFY_CLASSIC = ascii('-'.repeat(15));
/** Length byte 9, then event/argument pairs: weight 1, battery 2, timer 5, key 4. */
const NOTIFICATION_REQUEST = [0x09, 0x00, 0x01, 0x01, 0x02, 0x02, 0x05, 0x03, 0x04];

export const acaiaCommands = {
  identify: (layout: AcaiaLayoutKind) => encodeAcaiaFrame(AcaiaCommand.identify, layout === 'uart' ? IDENTIFY_UART : IDENTIFY_CLASSIC),
  notificationRequest: () => encodeAcaiaFrame(AcaiaCommand.event, NOTIFICATION_REQUEST),
  heartbeat: () => encodeAcaiaFrame(AcaiaCommand.heartbeat, [0x02, 0x00]),
  tare: () => encodeAcaiaFrame(AcaiaCommand.tare, [0x00]),
  startTimer: () => encodeAcaiaFrame(AcaiaCommand.timer, [0x00, 0x00]),
  stopTimer: () => encodeAcaiaFrame(AcaiaCommand.timer, [0x00, 0x02]),
  resetTimer: () => encodeAcaiaFrame(AcaiaCommand.timer, [0x00, 0x01]),
};

// ---------------------------------------------------------------- reassembly

/** One frame taken out of the stream: byte 2, and the payload from the length byte up to the checksums. */
export interface AcaiaRawFrame {
  kind: number;
  /** Starts with the length byte. */
  payload: Uint8Array;
  checksumOk: boolean;
  /** The whole frame, header to checksums. */
  bytes: Uint8Array;
}

export interface AcaiaBufferStats {
  /** Bytes dropped because no header came before them. */
  junkBytes: number;
  /** Headers whose length byte can't be right (0, or over 64). */
  badLength: number;
}

/**
 * Reassembles frames from notifications: a frame may arrive in pieces, and one notification may hold several. See
 * docs/acaia-protocol.md › Reassembly.
 */
export class AcaiaFrameBuffer {
  private buf: Uint8Array = new Uint8Array(0);
  readonly stats: AcaiaBufferStats = { junkBytes: 0, badLength: 0 };

  /** Bytes held while waiting for the rest of a frame. */
  get pending(): number {
    return this.buf.length;
  }

  clear(): void {
    this.buf = new Uint8Array(0);
  }

  /**
   * Appends a notification and returns every complete frame now in the buffer, in order. Frames failing their checksum
   * come back with checksumOk false unless `dropBadChecksum`: then only their header is dropped, and the search goes on
   * right behind it (a corrupt length byte mustn't swallow the frames after it).
   */
  push(bytes: ArrayLike<number>, opts: { dropBadChecksum?: boolean; onBadChecksum?: (frame: AcaiaRawFrame) => void } = {}): AcaiaRawFrame[] {
    const joined = new Uint8Array(this.buf.length + bytes.length);
    joined.set(this.buf, 0);
    for (let i = 0; i < bytes.length; i++) joined[this.buf.length + i] = bytes[i]! & 0xff;
    let b = joined;
    const frames: AcaiaRawFrame[] = [];
    for (;;) {
      const start = findHeader(b);
      if (start < 0) {
        // Keep a trailing EF: it may be the first half of a header split across notifications.
        const keep = b.length > 0 && b[b.length - 1] === ACAIA_HEADER1 ? 1 : 0;
        this.stats.junkBytes += b.length - keep;
        b = b.slice(b.length - keep);
        break;
      }
      if (start > 0) {
        this.stats.junkBytes += start;
        b = b.slice(start);
      }
      if (b.length < 4) break;
      const len = b[3]!;
      if (len < 1 || len > ACAIA_MAX_LENGTH_BYTE) {
        this.stats.badLength++;
        b = b.slice(2);
        continue;
      }
      const total = len + ACAIA_FRAME_OVERHEAD;
      if (b.length < total) break;
      const frameBytes = b.slice(0, total);
      const payload = frameBytes.slice(3, total - 2);
      const [even, odd] = acaiaChecksum(payload);
      const checksumOk = frameBytes[total - 2] === even && frameBytes[total - 1] === odd;
      const frame: AcaiaRawFrame = { kind: frameBytes[2]!, payload, checksumOk, bytes: frameBytes };
      if (!checksumOk && opts.dropBadChecksum) {
        opts.onBadChecksum?.(frame);
        b = b.slice(2);
        continue;
      }
      frames.push(frame);
      b = b.slice(total);
    }
    if (b.length > ACAIA_MAX_BUFFER) {
      this.stats.junkBytes += b.length - ACAIA_MAX_BUFFER;
      b = b.slice(b.length - ACAIA_MAX_BUFFER);
    }
    this.buf = b;
    return frames;
  }
}

function findHeader(b: Uint8Array): number {
  for (let i = 0; i + 1 < b.length; i++) if (b[i] === ACAIA_HEADER1 && b[i + 1] === ACAIA_HEADER2) return i;
  return -1;
}

// ---------------------------------------------------------------- messages

export interface AcaiaWeight {
  /** In the scale's display unit (grams unless its settings say ounces). -0 is reported as 0. */
  value: number;
  decimals: number;
  /** Bytes 2-3 of the weight body weren't zero (they are in every capture): the 16-bit value may be wrong. */
  highBytes: boolean;
}

export type AcaiaButton = 'tare' | 'start' | 'stop' | 'reset' | 'unknown';
export type AcaiaUnits = 'g' | 'oz' | 'unknown';

export type AcaiaMessage =
  | { type: 'weight'; weight: AcaiaWeight; timerMs: number | null; via: 'weight' | 'heartbeat' | 'button' }
  | { type: 'timer'; timerMs: number }
  | { type: 'button'; button: AcaiaButton }
  | { type: 'ack' }
  | { type: 'settings'; batteryPct: number; units: AcaiaUnits; unitsByte: number; timerRunning: boolean; autoOffMin: number; beep: boolean }
  | { type: 'info' }
  | { type: 'other'; kind: number; event: number | null }
  /** Too short for its type, or a weight with an unknown decimal byte. */
  | { type: 'malformed'; reason: 'short' | 'decimals' };

/** Record tags and the size of each record's body (see docs/acaia-protocol.md › Events). */
const RECORD_SIZES: Readonly<Record<number, number>> = {
  [AcaiaEvent.weight]: 6,
  [AcaiaEvent.battery]: 1,
  [AcaiaEvent.timer]: 3,
  [AcaiaEvent.button]: 1,
  [AcaiaEvent.ack]: 2,
};

/** The 6-byte weight body, or null when its decimal byte is outside 1-4. */
export function decodeAcaiaWeight(body: ArrayLike<number>): AcaiaWeight | null {
  const decimals = body[4]!;
  if (decimals < 1 || decimals > 4) return null;
  const raw = (body[0]! & 0xff) | ((body[1]! & 0xff) << 8);
  const magnitude = raw / 10 ** decimals;
  const negative = (body[5]! & 0x02) !== 0 && raw !== 0;
  return { value: negative ? -magnitude : magnitude, decimals, highBytes: body[2] !== 0 || body[3] !== 0 };
}

/** Minutes, seconds, tenths, in milliseconds. */
export function decodeAcaiaTimer(body: ArrayLike<number>): number {
  return ((body[0]! & 0xff) * 60 + (body[1]! & 0xff)) * 1000 + (body[2]! & 0xff) * 100;
}

interface Records {
  weightBody: Uint8Array | null;
  timerMs: number | null;
  key: number | null;
}

/** Walks a chain of [tag][body] records; an unknown tag or a cut-short body ends it. */
function walkRecords(data: Uint8Array): Records {
  const out: Records = { weightBody: null, timerMs: null, key: null };
  let i = 0;
  while (i < data.length) {
    const tag = data[i]!;
    const size = RECORD_SIZES[tag];
    if (size === undefined || i + 1 + size > data.length) break;
    const body = data.subarray(i + 1, i + 1 + size);
    if (tag === AcaiaEvent.weight && !out.weightBody) out.weightBody = body;
    else if (tag === AcaiaEvent.timer && out.timerMs === null) out.timerMs = decodeAcaiaTimer(body);
    else if (tag === AcaiaEvent.button && out.key === null) out.key = body[0]!;
    i += 1 + size;
  }
  return out;
}

const BUTTONS: Readonly<Record<number, AcaiaButton>> = { 0x00: 'tare', 0x08: 'start', 0x09: 'reset', 0x0a: 'stop' };

function weightMessage(body: Uint8Array, timerMs: number | null, via: 'weight' | 'heartbeat' | 'button'): AcaiaMessage {
  const weight = decodeAcaiaWeight(body);
  return weight ? { type: 'weight', weight, timerMs, via } : { type: 'malformed', reason: 'decimals' };
}

/** What a reassembled frame says. Never throws. */
export function parseAcaiaFrame(frame: Pick<AcaiaRawFrame, 'kind' | 'payload'>): AcaiaMessage {
  const p = frame.payload;
  if (frame.kind === AcaiaKind.settings) {
    if (p.length < 7) return { type: 'malformed', reason: 'short' };
    const unitsByte = p[2]!;
    return {
      type: 'settings',
      batteryPct: Math.min(100, p[1]! & 0x7f),
      timerRunning: (p[1]! & 0x80) !== 0,
      units: unitsByte === 0x02 ? 'g' : unitsByte === 0x05 ? 'oz' : 'unknown',
      unitsByte,
      autoOffMin: p[4]! * 5,
      beep: p[6] === 0x01,
    };
  }
  if (frame.kind === AcaiaKind.info) return { type: 'info' };
  if (frame.kind !== AcaiaKind.event) return { type: 'other', kind: frame.kind, event: null };
  if (p.length < 2) return { type: 'malformed', reason: 'short' };
  const event = p[1]!;
  const data = p.subarray(2);
  switch (event) {
    case AcaiaEvent.weight: {
      if (data.length < 6) return { type: 'malformed', reason: 'short' };
      return weightMessage(data.subarray(0, 6), walkRecords(data.subarray(6)).timerMs, 'weight');
    }
    case AcaiaEvent.timer:
      if (data.length < 3) return { type: 'malformed', reason: 'short' };
      return { type: 'timer', timerMs: decodeAcaiaTimer(data) };
    case AcaiaEvent.button: {
      if (data.length < 1) return { type: 'malformed', reason: 'short' };
      const records = walkRecords(new Uint8Array([AcaiaEvent.button, ...data]));
      // A press that carries the weight is still a press: the weight is only used for the packet log.
      return { type: 'button', button: BUTTONS[records.key ?? -1] ?? 'unknown' };
    }
    case AcaiaEvent.ack: {
      if (data.length < 2) return { type: 'malformed', reason: 'short' };
      const records = walkRecords(data.subarray(2));
      if (records.weightBody) return weightMessage(records.weightBody, records.timerMs, 'heartbeat');
      if (records.timerMs !== null) return { type: 'timer', timerMs: records.timerMs };
      return { type: 'ack' };
    }
    default:
      return { type: 'other', kind: frame.kind, event };
  }
}

/** "ef dd 0c …" */
export function acaiaHex(bytes: ArrayLike<number>): string {
  return Array.from({ length: bytes.length }, (_, i) => ((bytes[i] ?? 0) & 0xff).toString(16).padStart(2, '0')).join(' ');
}

/** "efdd0c…" or "ef dd 0c …" to bytes. */
export function fromAcaiaHex(hex: string): Uint8Array {
  const clean = hex.replace(/\s+/g, '');
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** An incoming frame in the documented layout, for tests and docs: kind, then the length byte and `body`. */
export function encodeAcaiaIncoming(kind: number, body: ArrayLike<number>): Uint8Array<ArrayBuffer> {
  return encodeAcaiaFrame(kind, [body.length + 1, ...Array.from(body)]);
}

/**
 * A weight event, for tests: `weight` in grams with `decimals` places (1 by default, as the Lunar shows), and the
 * Lunar 2021's trailing timer record when `timerMs` is given.
 */
export function encodeAcaiaWeightEvent(weight: number, opts: { decimals?: number; timerMs?: number } = {}): Uint8Array<ArrayBuffer> {
  const decimals = opts.decimals ?? 1;
  const raw = Math.round(Math.abs(weight) * 10 ** decimals);
  if (raw > 0xffff) throw new RangeError(`Acaia weight out of range: ${weight}`);
  const body = [AcaiaEvent.weight, raw & 0xff, raw >> 8, 0, 0, decimals, weight < 0 ? 0x02 : 0x00];
  if (opts.timerMs !== undefined) {
    const tenths = Math.round(opts.timerMs / 100);
    body.push(AcaiaEvent.timer, Math.floor(tenths / 600), Math.floor(tenths / 10) % 60, tenths % 10);
  }
  return encodeAcaiaIncoming(AcaiaKind.event, body);
}

/** A settings message, for tests: battery %, units byte (2 grams, 5 ounces). */
export function encodeAcaiaSettings(opts: { batteryPct?: number; unitsByte?: number; timerRunning?: boolean } = {}): Uint8Array<ArrayBuffer> {
  const battery = (opts.batteryPct ?? 93) | (opts.timerRunning ? 0x80 : 0);
  return encodeAcaiaIncoming(AcaiaKind.settings, [battery, opts.unitsByte ?? 0x02, 0x02, 0x01, 0x00, 0x01, 0x01, 0x00]);
}
